import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { format } from "date-fns";

export function useDashboardData(enterpriseId) {
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState([]);
  const [chartData, setChartData] = useState({ rondas: [], ocorrencias: [], inspecoes: [] });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    if (!enterpriseId) {
      setLoading(false);
      return;
    }

    let isMounted = true; // Evita setState após unmount

    const fetchData = async () => {
      setLoading(true);

      try {
        const today = new Date();
        const last7Days = [...Array(7)].map((_, i) =>
          format(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i), "yyyy-MM-dd")
        );

        const [roundsSnap, occurrencesSnap, inspectionsSnap] = await Promise.all([
          getDocs(query(collection(db, "rounds"), where("enterpriseId", "==", enterpriseId))),
          getDocs(query(collection(db, "occurrences"), where("enterpriseId", "==", enterpriseId))),
          getDocs(query(collection(db, "inspections"), where("enterpriseId", "==", enterpriseId))),
        ]);

        const rounds = [];
        const ocorrencias = [];
        const inspecoes = [];
        const ultimos = [];

        // 🔹 RONDAS
        roundsSnap.forEach((doc) => {
          const data = doc.data();
          rounds.push(data);

          const execDate = data.executedAt?.toDate?.() || data.finishedAt?.toDate?.();
          if (execDate) {
            ultimos.push({
              tipo: "Ronda",
              descricao: data.title || "Ronda realizada",
              data: execDate,
            });
          }
        });

        // 🔹 OCORRÊNCIAS
        occurrencesSnap.forEach((doc) => {
          const data = doc.data();
          ocorrencias.push(data);

          if (data.createdAt?.toDate) {
            ultimos.push({
              tipo: "Ocorrência",
              descricao: data.title || "Ocorrência registrada",
              data: data.createdAt.toDate(),
            });
          }
        });

        // 🔹 INSPEÇÕES
        inspectionsSnap.forEach((doc) => {
          const data = doc.data();
          inspecoes.push(data);

          if (data.executedAt?.toDate) {
            ultimos.push({
              tipo: "Inspeção",
              descricao: data.title || "Inspeção concluída",
              data: data.executedAt.toDate(),
            });
          }
        });

        const getDailyCount = (list, field) => {
          return last7Days
            .map((date) =>
              list.filter((item) => {
                const f = item[field];
                return f?.toDate && format(f.toDate(), "yyyy-MM-dd") === date;
              }).length
            )
            .reverse();
        };

        const summary = [
          {
            icon: "🔍",
            title: "Rondas Realizadas",
            realizadas: rounds.filter((r) => r.status === "completed").length,
            esperadas: rounds.length,
            percentual: ((rounds.filter((r) => r.status === "completed").length / (rounds.length || 1)) * 100).toFixed(1),
            ultimosRegistros: ultimos
              .filter((u) => u.tipo === "Ronda")
              .slice(0, 3)
              .map((u) => u.descricao),
          },
          {
            icon: "⚠️",
            title: "Ocorrências Registradas",
            realizadas: ocorrencias.length,
            esperadas: 10,
            percentual: ((ocorrencias.length / 10) * 100).toFixed(1),
            ultimosRegistros: ultimos
              .filter((u) => u.tipo === "Ocorrência")
              .slice(0, 3)
              .map((u) => u.descricao),
          },
          {
            icon: "✅",
            title: "Inspeções Concluídas",
            realizadas: inspecoes.filter((i) => i.status === "completed").length,
            esperadas: inspecoes.length,
            percentual: ((inspecoes.filter((i) => i.status === "completed").length / (inspecoes.length || 1)) * 100).toFixed(1),
            ultimosRegistros: ultimos
              .filter((u) => u.tipo === "Inspeção")
              .slice(0, 3)
              .map((u) => u.descricao),
          },
        ];

        if (isMounted) {
          setSummaryData(summary);
          setChartData({
            rondas: getDailyCount(rounds, "executedAt"),
            ocorrencias: getDailyCount(ocorrencias, "createdAt"),
            inspecoes: getDailyCount(inspecoes, "executedAt"),
          });

          const recent = ultimos
            .sort((a, b) => b.data.getTime() - a.data.getTime())
            .slice(0, 5)
            .map((r) => ({
              ...r,
              data: format(r.data, "dd/MM/yyyy"),
            }));

          setRecentActivity(recent);
        }
      } catch (err) {
        console.error("Erro ao buscar dados do dashboard:", err);
      }

      if (isMounted) setLoading(false);
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [enterpriseId]);

  return { loading, summaryData, chartData, recentActivity };
}
